/**
 * Per-tool icons (22x22 viewBox) — one glyph for every overlay tool.
 *
 * Lives in its own module because two unrelated surfaces draw the same artwork:
 * the DrawingBar (group buttons + flyouts) and the chart's right-click
 * "Draw Line" submenu. One copy means a tool can never end up with two
 * different faces depending on where the user reaches it from.
 *
 * Consumers size these via CSS — width/height in CSS override an SVG's
 * presentation attributes, so a 22px glyph drops into a 14px menu slot without
 * a second copy of the path data.
 */

const S = {
  line: {
    width: 22,
    height: 22,
    viewBox: '0 0 22 22',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.4,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  },
  fill: { width: 22, height: 22, viewBox: '0 0 22 22', fill: 'currentColor' },
}

const dot = (cx: number, cy: number) => (
  <circle cx={cx} cy={cy} r={1.7} fill="currentColor" stroke="none" />
)

export const ToolIcons: Record<string, React.ReactNode> = {
  // ── Lines ──
  straightLine: (
    <svg {...S.line}>
      <path d="M3 18L19 4" />
    </svg>
  ),
  segment: (
    <svg {...S.line}>
      <path d="M5 17L17 5" />
      {dot(5, 17)}
      {dot(17, 5)}
    </svg>
  ),
  rayLine: (
    <svg {...S.line}>
      <path d="M5 17L19 3" />
      {dot(5, 17)}
    </svg>
  ),
  horizontalStraightLine: (
    <svg {...S.line}>
      <path d="M3 11h16" />
    </svg>
  ),
  horizontalSegment: (
    <svg {...S.line}>
      <path d="M5 11h12" />
      {dot(5, 11)}
      {dot(17, 11)}
    </svg>
  ),
  horizontalRayLine: (
    <svg {...S.line}>
      <path d="M5 11h14" />
      {dot(5, 11)}
    </svg>
  ),
  verticalStraightLine: (
    <svg {...S.line}>
      <path d="M11 3v16" />
    </svg>
  ),
  verticalSegment: (
    <svg {...S.line}>
      <path d="M11 5v12" />
      {dot(11, 5)}
      {dot(11, 17)}
    </svg>
  ),
  verticalRayLine: (
    <svg {...S.line}>
      <path d="M11 3v14" />
      {dot(11, 17)}
    </svg>
  ),
  priceLine: (
    <svg {...S.line}>
      <path d="M3 11h10" />
      <rect x="13" y="8" width="6" height="6" rx="1" />
    </svg>
  ),
  // ── Channels ──
  parallelStraightLine: (
    <svg {...S.line}>
      <path d="M3 15L19 6M3 18L19 9" />
    </svg>
  ),
  priceChannelLine: (
    <svg {...S.line}>
      <path d="M3 13L19 4M3 16L19 7M3 19L19 10" />
    </svg>
  ),
  // ── Fibonacci ──
  fibonacciLine: (
    <svg {...S.line}>
      <path d="M4 5h14M4 9h14M4 13h14M4 17h14" />
    </svg>
  ),
  // ── Position ──
  // Long / Short: a two-band box where the TARGET band is filled and carries a
  // directional arrow — top for long, bottom for short. Monochrome
  // (`currentColor`) so it reads as one family with the rest of the toolbar and
  // inherits the active/hover treatment; the green/red P&L semantics live on the
  // actual chart overlay (riskRewardOverlay), not on a 22px icon.
  longPositionTool: (
    <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
      <rect
        x="4"
        y="4.5"
        width="14"
        height="6"
        rx="1"
        fill="currentColor"
        fillOpacity={0.28}
        stroke="currentColor"
        strokeWidth={1.2}
      />
      <rect
        x="4"
        y="11.5"
        width="14"
        height="6"
        rx="1"
        stroke="currentColor"
        strokeWidth={1.2}
        strokeOpacity={0.4}
      />
      <path
        d="M11 9.5V5.6M9.4 7l1.6-1.6L12.6 7"
        stroke="currentColor"
        strokeWidth={1.3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  shortPositionTool: (
    <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
      <rect
        x="4"
        y="4.5"
        width="14"
        height="6"
        rx="1"
        stroke="currentColor"
        strokeWidth={1.2}
        strokeOpacity={0.4}
      />
      <rect
        x="4"
        y="11.5"
        width="14"
        height="6"
        rx="1"
        fill="currentColor"
        fillOpacity={0.28}
        stroke="currentColor"
        strokeWidth={1.2}
      />
      <path
        d="M11 12.5v3.9M9.4 15l1.6 1.6L12.6 15"
        stroke="currentColor"
        strokeWidth={1.3}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  // Position Plan: two horizontal levels (entry solid, stop dashed) joined by
  // a sizing bracket — the two-click entry→stop gesture in miniature.
  positionPlan: (
    <svg {...S.line}>
      <path d="M3 6.5h16" />
      <path d="M3 15.5h3M8 15.5h3M13 15.5h3M18 15.5h1" />
      <path d="M11 6.5v9" />
      <path d="M8.5 9.5L11 6.5l2.5 3M8.5 12.5l2.5 3 2.5-3" />
    </svg>
  ),
  // ── Measure ──
  measure: (
    <svg {...S.line}>
      <path d="M3 13.5L13.5 3l5.5 5.5L8.5 19z" />
      <path d="M7 8.5l1.5 1.5M10 5.5l1.5 1.5M12.5 11.5l1.5 1.5" />
    </svg>
  ),
  dateRange: (
    <svg {...S.line}>
      <path d="M4 7v8M18 7v8" />
      <path d="M4 11h14" />
      <path d="M7.5 8.5L4 11l3.5 2.5M14.5 8.5L18 11l-3.5 2.5" />
    </svg>
  ),
  priceRange: (
    <svg {...S.line}>
      <path d="M7 4h8M7 18h8" />
      <path d="M11 4v14" />
      <path d="M8.5 7.5L11 4l2.5 3.5M8.5 14.5L11 18l2.5-3.5" />
    </svg>
  ),
  datePriceRange: (
    <svg {...S.line}>
      <rect x="4" y="5" width="14" height="12" rx="1" />
      <path d="M8 15l6-6" />
      <path d="M14 12.5V9h-3.5" />
    </svg>
  ),
  // ── Arrows ──
  arrow: (
    <svg {...S.line}>
      <path d="M4 18L17 5" />
      <path d="M11 5h6v6" />
    </svg>
  ),
  arrowMarkUp: (
    <svg {...S.line}>
      <path d="M11 18V5" />
      <path d="M6.5 9.5L11 5l4.5 4.5" />
    </svg>
  ),
  arrowMarkDown: (
    <svg {...S.line}>
      <path d="M11 5v13" />
      <path d="M6.5 13.5L11 18l4.5-4.5" />
    </svg>
  ),
  arrowMarkLeft: (
    <svg {...S.line}>
      <path d="M18 11H5" />
      <path d="M9.5 6.5L5 11l4.5 4.5" />
    </svg>
  ),
  arrowMarkRight: (
    <svg {...S.line}>
      <path d="M4 11h13" />
      <path d="M12.5 6.5L17 11l-4.5 4.5" />
    </svg>
  ),
  // ── Shapes ──
  rect: (
    <svg {...S.line}>
      <rect x="4" y="5" width="14" height="12" rx="1" />
    </svg>
  ),
  circle: (
    <svg {...S.line}>
      <circle cx="11" cy="11" r="7" />
    </svg>
  ),
  ellipse: (
    <svg {...S.line}>
      <ellipse cx="11" cy="11" rx="8" ry="5.5" />
    </svg>
  ),
  triangle: (
    <svg {...S.line}>
      <path d="M11 4l7 12H4z" />
    </svg>
  ),
  polygon: (
    <svg {...S.line}>
      <path d="M11 3.5l7.5 5.4-2.9 8.8H6.4L3.5 8.9z" />
    </svg>
  ),
  polyline: (
    <svg {...S.line}>
      <path d="M3 15l4-6 4 4 3-6 4 5" />
    </svg>
  ),
  arc: (
    <svg {...S.line}>
      <path d="M4 17a10 10 0 0114 0" />
    </svg>
  ),
  // ── Annotations ──
  simpleAnnotation: (
    <svg {...S.line}>
      <path d="M4 5h14v9H9l-3 3v-3H4z" />
    </svg>
  ),
  simpleTag: (
    <svg {...S.line}>
      <path d="M4 8l5-4h9v9l-5 4z" />
      {dot(9, 8)}
    </svg>
  ),
  text: (
    <svg width={22} height={22} viewBox="0 0 22 22" fill="currentColor">
      <text
        x="11"
        y="16"
        textAnchor="middle"
        fontSize="14"
        fontWeight="600"
        fontFamily="sans-serif"
      >
        T
      </text>
    </svg>
  ),
}
